import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Sparkles, Loader2, AlertCircle, KeyRound, LogOut } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API, useAuth } from "@/context/AuthContext";
import useDocumentTitle from "@/hooks/useDocumentTitle";

const ChangePasswordPage = () => {
  const { user, loading, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [currentPwd, setCurrentPwd] = useState("");
  const [newPwd, setNewPwd] = useState("");
  const [confirmPwd, setConfirmPwd] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useDocumentTitle("Trocar senha");

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;

  // If the user already has a strong password (no force flag), allow opt-in
  // visit to /trocar-senha but show the same form. Either way we redirect home
  // after success.

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (newPwd.length < 6) {
      setError("A nova senha precisa ter ao menos 6 caracteres.");
      return;
    }
    if (newPwd !== confirmPwd) {
      setError("As senhas digitadas não coincidem.");
      return;
    }
    if (newPwd === currentPwd) {
      setError("A nova senha precisa ser diferente da atual.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPwd,
        new_password: newPwd,
      });
      await refreshUser();
      toast.success("Senha atualizada com sucesso!");
      navigate("/", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Não foi possível atualizar a senha.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const forced = Boolean(user.must_change_password);

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-brand-gradient flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.4} />
            </div>
            <div>
              <div className="heading text-sm font-semibold text-slate-900 leading-tight">
                Grid<span className="text-brand-blue">Recruiter</span>
              </div>
              <div className="text-[10px] text-slate-500 tracking-[0.16em] uppercase">
                Trocar senha
              </div>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            data-testid="change-pwd-logout-button"
            onClick={handleLogout}
            className="text-slate-600 hover:text-red-600 hover:bg-red-50"
          >
            <LogOut className="w-4 h-4 mr-1.5" /> Sair
          </Button>
        </div>
      </header>

      {/* Main */}
      <main className="flex-1 flex items-center justify-center px-6 py-10">
        <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl shadow-sm p-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <KeyRound className="w-5 h-5 text-brand-blue" strokeWidth={2.2} />
            </div>
            <div>
              <h1 className="heading text-xl font-semibold text-slate-900 leading-tight">
                {forced ? "Defina sua nova senha" : "Alterar senha"}
              </h1>
              <p className="text-sm text-slate-600 mt-1">
                {forced ? (
                  <>
                    Olá, <strong>{user.name}</strong>! Como este é seu primeiro acesso, troque a
                    senha temporária por uma de sua preferência para continuar.
                  </>
                ) : (
                  "Para sua segurança, escolha uma senha que só você saiba."
                )}
              </p>
            </div>
          </div>

          {error && (
            <div className="mb-4 flex items-start gap-2 px-3 py-2.5 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="current-pwd">Senha atual</Label>
              <Input
                id="current-pwd"
                data-testid="current-password-input"
                type="password"
                value={currentPwd}
                onChange={(e) => setCurrentPwd(e.target.value)}
                placeholder={forced ? "A senha temporária recebida" : "Sua senha atual"}
                required
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="new-pwd">Nova senha</Label>
              <Input
                id="new-pwd"
                data-testid="new-password-input"
                type="password"
                value={newPwd}
                onChange={(e) => setNewPwd(e.target.value)}
                placeholder="Mínimo 6 caracteres"
                required
                minLength={6}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="confirm-pwd">Confirme a nova senha</Label>
              <Input
                id="confirm-pwd"
                data-testid="confirm-password-input"
                type="password"
                value={confirmPwd}
                onChange={(e) => setConfirmPwd(e.target.value)}
                placeholder="Digite novamente"
                required
                minLength={6}
              />
            </div>

            <Button
              type="submit"
              data-testid="change-password-submit"
              disabled={submitting}
              className="w-full bg-brand-gradient text-white py-2.5 border-0 mt-2"
            >
              {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
              Salvar nova senha
            </Button>
          </form>

          {!forced && (
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-xs text-slate-500 hover:text-slate-700 mt-5 block mx-auto"
              data-testid="change-pwd-cancel"
            >
              Cancelar
            </button>
          )}
        </div>
      </main>
    </div>
  );
};

export default ChangePasswordPage;
